import { DBSchema, IDBPDatabase, openDB } from 'idb'

export type UserProfile = {
  id: string
  role: string
  email: string
  name?: string
}

interface TokenDB extends DBSchema {
  tokens: {
    key: string
    value: {
      accessToken: string
      refreshToken: string
      expiresAt: number
    }
  }
  settings: {
    key: string
    value: string
  }
  user: {
    key: string
    value: UserProfile
  }
}

class TokenStore {
  private db: Promise<IDBPDatabase<TokenDB>> | null = null
  private readonly DB_NAME = 'moovy-auth-tokens'
  // settings/user ストア追加前に作成された v1 DB も upgrade を通して補完する。
  private readonly DB_VERSION = 2
  private readonly STORE_NAME = 'tokens'
  private readonly TOKEN_KEY = 'current'
  private readonly USER_STORE_NAME = 'user'

  constructor() {
    // Don't initialize DB in constructor, do it lazily
  }

  private async initDB(): Promise<IDBPDatabase<TokenDB>> {
    // Check if we're in the browser environment
    if (typeof window === 'undefined' || !window.indexedDB) {
      throw new Error('IndexedDB is not available in this environment')
    }

    return openDB<TokenDB>(this.DB_NAME, this.DB_VERSION, {
      upgrade(db, oldVersion) {
        if (!db.objectStoreNames.contains('tokens')) {
          db.createObjectStore('tokens')
        }
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings')
        }
        if (!db.objectStoreNames.contains('user')) {
          db.createObjectStore('user')
          console.log(oldVersion)
        }
        // ... existing code ...
      },
    })
  }

  private async getDB(): Promise<IDBPDatabase<TokenDB>> {
    // Lazy initialization with browser check
    if (!this.db) {
      if (typeof window === 'undefined' || !window.indexedDB) {
        throw new Error('IndexedDB is not available in this environment')
      }
      this.db = this.initDB()
    }
    return this.db
  }

  async setTokens(accessToken: string, refreshToken: string, expiresIn: number): Promise<void> {
    try {
      const db = await this.getDB()
      await db.put(
        this.STORE_NAME,
        {
          accessToken: this.obfuscate(accessToken),
          refreshToken: this.obfuscate(refreshToken),
          expiresAt: Date.now() + expiresIn * 1000,
        },
        this.TOKEN_KEY,
      )
    } catch (error) {
      console.error('Failed to store tokens in IndexedDB:', error)
      throw error
    }
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const db = await this.getDB()
      const data = await db.get(this.STORE_NAME, this.TOKEN_KEY)

      if (!data) {
        return null
      }

      // Check if token is expired
      if (Date.now() > data.expiresAt) {
        return null
      }

      return this.deobfuscate(data.accessToken)
    } catch (error) {
      console.error('Failed to get access token from IndexedDB:', error)
      return null
    }
  }

  async getRefreshToken(): Promise<string | null> {
    try {
      const db = await this.getDB()
      const data = await db.get(this.STORE_NAME, this.TOKEN_KEY)

      if (!data) {
        return null
      }

      return this.deobfuscate(data.refreshToken)
    } catch (error) {
      console.error('Failed to get refresh token from IndexedDB:', error)
      return null
    }
  }

  async clearTokens(): Promise<void> {
    try {
      const db = await this.getDB()
      await db.delete(this.STORE_NAME, this.TOKEN_KEY)
    } catch (error) {
      console.error('Failed to clear tokens from IndexedDB:', error)
    }
  }

  // ... existing code ...

  async isTokenExpired(): Promise<boolean> {
    try {
      const db = await this.getDB()
      const data = await db.get(this.STORE_NAME, this.TOKEN_KEY)

      if (!data) {
        return true
      }

      return Date.now() > data.expiresAt
    } catch (error) {
      console.error('Failed to check token expiration:', error)
      return true
    }
  }

  private obfuscate(token: string): string {
    // Simple obfuscation - reverse the base64 encoded token
    // This is not encryption, just makes it less obvious in dev tools
    const salt = process.env.NEXT_PUBLIC_TOKEN_SALT || 'hr-ai-agent-2024'
    const combined = salt + token

    // Use Buffer in Node.js, btoa in browser
    if (typeof window === 'undefined') {
      return Buffer.from(combined).toString('base64').split('').reverse().join('')
    } else {
      return btoa(combined).split('').reverse().join('')
    }
  }

  private deobfuscate(obfuscatedToken: string): string {
    try {
      const salt = process.env.NEXT_PUBLIC_TOKEN_SALT || 'hr-ai-agent-2024'

      // Use Buffer in Node.js, atob in browser
      let decoded: string
      if (typeof window === 'undefined') {
        decoded = Buffer.from(obfuscatedToken.split('').reverse().join(''), 'base64').toString()
      } else {
        decoded = atob(obfuscatedToken.split('').reverse().join(''))
      }

      // Remove salt from the beginning
      return decoded.substring(salt.length)
    } catch (error) {
      console.error('Failed to deobfuscate token:', error)
      return ''
    }
  }

  // User profile management methods
  async setUser(profile: UserProfile): Promise<void> {
    try {
      const db = await this.getDB()
      await db.put(this.USER_STORE_NAME, profile, this.TOKEN_KEY)
    } catch (error) {
      console.error('Failed to store user in IndexedDB:', error)
      throw error
    }
  }

  async getUser(): Promise<UserProfile | null> {
    try {
      const db = await this.getDB()
      const user = await db.get(this.USER_STORE_NAME, this.TOKEN_KEY)
      return user || null
    } catch (error) {
      console.error('Failed to get user from IndexedDB:', error)
      return null
    }
  }

  async getUserId(): Promise<string | null> {
    try {
      const user = await this.getUser()
      return user?.id ?? null
    } catch (error) {
      console.error('Failed to get userId from IndexedDB:', error)
      return null
    }
  }

  async clearUser(): Promise<void> {
    try {
      const db = await this.getDB()
      await db.delete(this.USER_STORE_NAME, this.TOKEN_KEY)
    } catch (error) {
      console.error('Failed to clear user from IndexedDB:', error)
    }
  }

  // Settings management methods
  async setSetting(key: string, value: string): Promise<void> {
    try {
      const db = await this.getDB()
      await db.put('settings', value, key)
    } catch (error) {
      console.error(`Failed to store setting ${key} in IndexedDB:`, error)
      throw error
    }
  }

  async getSetting(key: string): Promise<string | null> {
    try {
      const db = await this.getDB()
      const value = await db.get('settings', key)
      return value || null
    } catch (error) {
      console.error(`Failed to get setting ${key} from IndexedDB:`, error)
      return null
    }
  }

  async removeSetting(key: string): Promise<void> {
    try {
      const db = await this.getDB()
      await db.delete('settings', key)
    } catch (error) {
      console.error(`Failed to remove setting ${key} from IndexedDB:`, error)
    }
  }

  // Tenant ID specific methods for convenience
  async setTenantId(tenantId: string): Promise<void> {
    return this.setSetting('SWITCH_TENANT_ID', tenantId)
  }

  async getTenantId(): Promise<string | null> {
    return this.getSetting('SWITCH_TENANT_ID')
  }

  async removeTenantId(): Promise<void> {
    return this.removeSetting('SWITCH_TENANT_ID')
  }

  // Session ID specific methods for single-session enforcement
  async setSessionId(sessionId: string): Promise<void> {
    return this.setSetting('SESSION_ID', sessionId)
  }

  async getSessionId(): Promise<string | null> {
    return this.getSetting('SESSION_ID')
  }

  async removeSessionId(): Promise<void> {
    return this.removeSetting('SESSION_ID')
  }

  // Clear all data from IndexedDB
  async clearAll(): Promise<void> {
    try {
      const db = await this.getDB()
      await db.clear('tokens')
      await db.clear('settings')
      await db.clear('user')
    } catch (error) {
      console.error('Failed to clear all data from IndexedDB:', error)
    }
  }
}

// Export singleton instance (null on server, instance on client)
let tokenStoreInstance: TokenStore | null = null

// Export a getter function that safely returns the token store
export function getTokenStore(): TokenStore {
  if (typeof window === 'undefined') {
    // Return a mock implementation for SSR that returns safe defaults
    return {
      setTokens: async () => {
        throw new Error('TokenStore not available on server')
      },
      getAccessToken: async () => null,
      getRefreshToken: async () => null,
      clearTokens: async () => {},
      isTokenExpired: async () => true,
      setUser: async () => {
        throw new Error('TokenStore not available on server')
      },
      getUser: async () => null,
      getUserId: async () => null,
      clearUser: async () => {},
      setSetting: async () => {
        throw new Error('TokenStore not available on server')
      },
      getSetting: async () => null,
      removeSetting: async () => {},
      setTenantId: async () => {
        throw new Error('TokenStore not available on server')
      },
      getTenantId: async () => null,
      removeTenantId: async () => {},
      setSessionId: async () => {
        throw new Error('TokenStore not available on server')
      },
      getSessionId: async () => null,
      removeSessionId: async () => {},
      clearAll: async () => {},
    } as unknown as TokenStore
  }

  if (!tokenStoreInstance) {
    tokenStoreInstance = new TokenStore()
  }
  return tokenStoreInstance
}

// Export a more convenient singleton
export async function logout(): Promise<void> {
  try {
    // SSR/非ブラウザ環境ではモックが返るため安全に呼べる
    const store = getTokenStore()

    // 可能な限り確実に全データを削除
    await Promise.allSettled([store.clearTokens(), store.clearUser(), store.removeTenantId()])

    // 念のため全ストアもクリア
    await store.clearAll()
  } catch (e) {
    // ログアウトは失敗してもユーザーにブロッキングしない
    console.error('logout failed:', e)
  }
}
